import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus } from 'lucide-react';
import type { Organization, Page } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';
import { NewOrganizationWizard } from './organizations/NewOrganizationWizard';

// The slug rule moved to lib/onboarding with the wizard (2026-09-17); this
// re-export keeps OrganizationDetail's import where it was.
export { slugify } from '../lib/onboarding';

export function OrganizationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /**
   * This page is reached by two very different people since the Administration
   * menu stopped hiding it from org admins.
   *
   * `org.manage` is the platform's — creating a tenant, suspending one — and
   * no org role can ever hold it. Without it the list is the caller's own
   * organization, one row, and the way to the page where its contact details
   * are edited. Everything below asks which of the two is reading rather than
   * offering a button whose only outcome is a 403.
   */
  const { can } = useSession();
  const platform = can('org.manage');
  /**
   * Creating a tenant is the New organization wizard (2026-09-17): the
   * organization AND its first administrator, reviewed against a dry run
   * before anything is written. The inline name-and-slug form it replaced
   * made an organization nobody could sign in to until a second form was
   * filled on another page.
   */
  const [wizardOpen, setWizardOpen] = useState(false);

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<Page<Organization>>('/orgs'),
  });

  const toggleStatus = useMutation({
    mutationFn: (org: Organization) =>
      api.patch<Organization>(`/orgs/${org.id}`, {
        status: org.status === 'active' ? 'suspended' : 'active',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orgs'] }),
  });

  return (
    <>
      <PageHeader
        title={platform ? 'Organizations' : 'Your organization'}
        description={
          platform
            ? 'Tenants on this platform. Devices, gateways and users all belong to exactly one.'
            : // Not "tenants on this platform": that sentence answers a question
              // this reader is not shown the answer to, and the list below is
              // only ever their own row.
              'Your company’s record on this platform. Open it to see what it holds and to keep its contact details current.'
        }
        actions={
          // Creating a tenant is `org.manage`, which no org role can hold.
          platform ? (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setWizardOpen(true)}>
              New organization
            </Button>
          ) : null
        }
      />

      {wizardOpen && <NewOrganizationWizard onClose={() => setWizardOpen(false)} />}

      <Card>
        {orgsQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : orgsQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={orgsQuery.error} />
          </div>
        ) : (orgsQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon={<Building2 className="size-8" />}
            title="No organizations yet"
            description="Create one to start assigning ONUs and gateways to a tenant."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">#</th>
              <th className="th">Name</th>
              <th className="th">Slug</th>
              <th className="th">Status</th>
              <th className="th">ONUs</th>
              <th className="th">Gateways</th>
              <th className="th">Users</th>
              <th className="th">Created</th>
              <th className="th" />
            </THead>
            <TBody>
              {orgsQuery.data?.items.map((org) => (
                // The row opens the tenant's own page — where the counts that
                // decide a suspend or a delete actually live.
                <TRow
                  key={org.id}
                  onClick={() => navigate(`/organizations/${org.number ?? org.id}`)}
                >
                  <td className="td text-sm tabular-nums text-muted">#{org.number ?? '—'}</td>
                  <td className="td font-medium">{org.name}</td>
                  <td className="td font-mono text-xs text-muted">{org.slug}</td>
                  <td className="td">
                    <Pill state tone={org.status === 'active' ? 'success' : 'warn'} dot>
                      {org.status}
                    </Pill>
                  </td>
                  <td className="td tabular-nums">{org.deviceCount ?? 0}</td>
                  <td className="td tabular-nums">{org.siteCount ?? 0}</td>
                  <td className="td tabular-nums">{org.userCount ?? 0}</td>
                  <td className="td text-muted">{formatTimestamp(org.createdAt)}</td>
                  <td className="td text-right">
                    {/*
                      Suspending is the platform's act, not the tenant's, and
                      the API refuses `status` to anyone without `org.manage`.
                      An org admin — who reaches this list to see their own row
                      — would otherwise get a button that could only 403. The
                      row still opens their organization, where what they CAN
                      edit is.
                    */}
                    {platform && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          // The row itself navigates; the toggle must not.
                          e.stopPropagation();
                          toggleStatus.mutate(org);
                        }}
                      >
                        {org.status === 'active' ? 'Suspend' : 'Activate'}
                      </Button>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </>
  );
}
